import type { PartyLedgerLine, PartyLedgerTypeFilter } from "@/lib/parties/partyLedgerStore";

export type PartyLedgerCategory =
  | "car_sold"
  | "payment_received"
  | "refund_paid"
  | "car_exchange"
  | "exchange_adjustment"
  | "opening_balance"
  | "other_adjustment";

export type PartyLedgerCategoryMeta = {
  id: PartyLedgerCategory;
  label: string;
};

/** Internal labels for summary/icons only — never shown as the transaction title when particulars exist. */
export const PARTY_LEDGER_CATEGORIES: Record<PartyLedgerCategory, PartyLedgerCategoryMeta> = {
  car_sold: { id: "car_sold", label: "Car Sold" },
  payment_received: { id: "payment_received", label: "Payment Received" },
  refund_paid: { id: "refund_paid", label: "Refund Paid" },
  car_exchange: { id: "car_exchange", label: "Car Exchange" },
  exchange_adjustment: { id: "exchange_adjustment", label: "Exchange Adjustment" },
  opening_balance: { id: "opening_balance", label: "Opening Balance" },
  other_adjustment: { id: "other_adjustment", label: "Other Adjustment" },
};

export type PartyRoleHint = "customer" | "supplier" | "investor" | "unknown";

export type ClassifiedPartyLedgerLine = {
  line: PartyLedgerLine;
  category: PartyLedgerCategory;
  /** Dynamic headline from journal particulars / product (not a hardcoded phrase). */
  categoryLabel: string;
  title: string;
  /** Extra dynamic lines from particulars (what the amount is for). */
  purpose: string | null;
  detail: string | null;
  saleId: number | null;
  /** Signed movement: debit positive, credit negative (matches GL running balance). */
  signedAmount: number;
  balance: number;
  balanceSide: "Dr" | "Cr";
  statusNote: string | null;
};

export type PartyLedgerSummary = {
  openingBalance: number;
  openingBalanceSide: "Dr" | "Cr";
  totalSales: number;
  totalReceipts: number;
  /** Cash refunds + exchange/return reversals + other non-sale/non-receipt movements (absolute sum). */
  totalRefundsAdjustments: number;
  currentBalance: number;
  currentBalanceSide: "Dr" | "Cr";
  currentBalanceLabel: string;
  settled: boolean;
};

function joinedParticulars(line: Pick<PartyLedgerLine, "particulars">): string {
  return (line.particulars ?? []).join(" · ");
}

function voucherPrefix(voucher: string | null | undefined): string {
  const raw = String(voucher ?? "").trim().toUpperCase();
  const match = /^([A-Z]+)/.exec(raw);
  return match?.[1] ?? "";
}

export function extractSaleId(text: string): number | null {
  const match = /Sale\s*#\s*(\d+)/i.exec(text);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Normalize particulars into clean dynamic display lines (as stored on the journal).
 */
export function dynamicParticularLines(
  line: Pick<PartyLedgerLine, "particulars" | "productName" | "voucher">,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const raw of line.particulars ?? []) {
    const text = String(raw ?? "").trim();
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }

  const product = line.productName?.trim();
  if (product) {
    const key = product.toLowerCase();
    if (!seen.has(key) && !out.some((p) => p.toLowerCase().includes(key))) {
      out.push(product);
    }
  }

  return out;
}

/**
 * Headline + purpose taken only from live ledger data (particulars / product / voucher).
 */
export function buildDynamicDescriptions(
  line: Pick<PartyLedgerLine, "particulars" | "productName" | "voucher">,
): { headline: string; purpose: string | null } {
  const lines = dynamicParticularLines(line);
  if (lines.length === 0) {
    const voucher = line.voucher?.trim();
    return {
      headline: voucher || "Transaction",
      purpose: line.productName?.trim() || null,
    };
  }

  const headline = lines[0];
  const rest = lines.slice(1).filter((p) => p.toLowerCase() !== headline.toLowerCase());
  return {
    headline,
    purpose: rest.length > 0 ? rest.join(" · ") : null,
  };
}

/** Classify for icons + summary buckets only — does not invent display text. */
export function detectPartyLedgerCategory(
  line: PartyLedgerLine,
  role: PartyRoleHint = "customer",
): PartyLedgerCategory {
  const text = joinedParticulars(line);
  const lower = text.toLowerCase();
  const prefix = voucherPrefix(line.voucher);
  const debit = Number(line.debit ?? 0);
  const credit = Number(line.credit ?? 0);

  if (/car\s+exchange\s+reverse/i.test(text)) return "exchange_adjustment";
  if (/car\s+exchange\s+refund/i.test(text)) return "car_exchange";
  if (/sale\s+return\s+refund/i.test(text) || (/refund/i.test(text) && (prefix === "BPV" || prefix === "CPV"))) {
    return "refund_paid";
  }
  if (
    /customer\s+receivable\s*[–-]\s*sale/i.test(text) ||
    (prefix === "SV" && (debit > 0 || /receivable/i.test(lower)))
  ) {
    return "car_sold";
  }
  if (/sale\s+collection/i.test(text) || prefix === "CRV" || prefix === "BRV") {
    return "payment_received";
  }
  if (prefix === "CPV" || prefix === "BPV") {
    return role === "supplier" || role === "investor" ? "other_adjustment" : "refund_paid";
  }
  if (prefix === "SV" && debit > 0) return "car_sold";
  if (role === "customer" && debit > 0 && credit < 0.005 && /receivable|due from/i.test(lower)) {
    return "car_sold";
  }
  if (role === "customer" && credit > 0 && debit < 0.005 && /received|collection/i.test(lower)) {
    return "payment_received";
  }

  return "other_adjustment";
}

/** @deprecated Use buildDynamicDescriptions — kept for older imports/tests. */
export function buildPurposeDescription(
  line: Pick<PartyLedgerLine, "particulars" | "productName">,
  _categoryLabel: string,
  _title: string,
): string | null {
  return buildDynamicDescriptions(line).purpose;
}

/** @deprecated Display text is dynamic from particulars; category labels are internal only. */
export function otherAdjustmentDisplayLabel(
  text: string,
  _signedAmount: number,
  _role: PartyRoleHint,
): string {
  const first = text.split(/\s*·\s*/)[0]?.trim();
  return first || "Transaction";
}

export function classifyPartyLedgerLine(
  line: PartyLedgerLine,
  role: PartyRoleHint = "customer",
): Omit<ClassifiedPartyLedgerLine, "statusNote"> {
  const text = joinedParticulars(line);
  const saleId = line.saleId && line.saleId > 0 ? line.saleId : extractSaleId(text);
  const debit = Number(line.debit ?? 0);
  const credit = Number(line.credit ?? 0);
  const signedAmount = Math.round((debit - credit) * 100) / 100;

  const category = detectPartyLedgerCategory(line, role);
  const { headline, purpose } = buildDynamicDescriptions(line);

  return {
    line,
    category,
    categoryLabel: headline,
    title: headline,
    purpose,
    detail: purpose,
    saleId,
    signedAmount,
    balance: Number(line.balance ?? 0),
    balanceSide: line.balanceSide === "Cr" ? "Cr" : "Dr",
  };
}

export function balanceWording(
  amount: number,
  side: "Dr" | "Cr",
  role: PartyRoleHint,
): { label: string; settled: boolean } {
  const abs = Math.abs(amount);
  if (abs < 0.005) {
    return { label: "Settled", settled: true };
  }

  if (role === "supplier") {
    if (side === "Cr") return { label: "Amount Payable", settled: false };
    return { label: "Supplier Advance", settled: false };
  }

  if (role === "investor") {
    if (side === "Dr") return { label: "Amount Receivable", settled: false };
    return { label: "Amount Payable", settled: false };
  }

  if (side === "Dr") return { label: "Amount Due", settled: false };
  return { label: "Customer Advance", settled: false };
}

export function formatMoneyPlain(amount: number, symbol = ""): string {
  const formatted = Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol ? `${symbol} ` : ""}${formatted}`;
}

/** Compact ledger: no amount notes under the date (balance column already shows that). */
export function statusNoteForLine(
  _classified: Omit<ClassifiedPartyLedgerLine, "statusNote">,
  _role: PartyRoleHint,
  _symbol: string,
): string | null {
  return null;
}

export function enrichPartyLedgerLines(
  lines: PartyLedgerLine[],
  role: PartyRoleHint,
  _symbol = "",
): ClassifiedPartyLedgerLine[] {
  return lines.map((line) => {
    const base = classifyPartyLedgerLine(line, role);
    return {
      ...base,
      statusNote: null,
    };
  });
}

/**
 * Summary cards from filtered ledger data.
 * Outstanding always comes from backend closing balance (never recomputed from category sums),
 * so exchange reverse + replacement sale + refund cannot double-count into the balance card.
 */
export function buildPartyLedgerSummary(input: {
  role: PartyRoleHint;
  openingBalance: number;
  openingBalanceSide: "Dr" | "Cr";
  currentBalance: number;
  currentBalanceSide: "Dr" | "Cr";
  lines: PartyLedgerLine[];
  symbol?: string;
}): PartyLedgerSummary {
  const classified = input.lines.map((line) => classifyPartyLedgerLine(line, input.role));

  let totalSales = 0;
  let totalReceipts = 0;
  let totalRefundsAdjustments = 0;

  for (const row of classified) {
    const abs = Math.abs(row.signedAmount);
    switch (row.category) {
      case "car_sold":
        totalSales = Math.round((totalSales + abs) * 100) / 100;
        break;
      case "payment_received":
        totalReceipts = Math.round((totalReceipts + abs) * 100) / 100;
        break;
      case "refund_paid":
      case "car_exchange":
      case "exchange_adjustment":
      case "other_adjustment":
        totalRefundsAdjustments = Math.round((totalRefundsAdjustments + abs) * 100) / 100;
        break;
      default:
        break;
    }
  }

  const wording = balanceWording(input.currentBalance, input.currentBalanceSide, input.role);

  return {
    openingBalance: input.openingBalance,
    openingBalanceSide: input.openingBalanceSide,
    totalSales,
    totalReceipts,
    totalRefundsAdjustments,
    currentBalance: input.currentBalance,
    currentBalanceSide: input.currentBalanceSide,
    currentBalanceLabel: wording.label,
    settled: wording.settled,
  };
}

export function resolvePartyRole(
  typeFilter: PartyLedgerTypeFilter,
  partyType?: string | null,
): PartyRoleHint {
  if (typeFilter === "customer" || typeFilter === "supplier" || typeFilter === "investor") {
    return typeFilter;
  }
  const t = (partyType ?? "").toLowerCase();
  if (t === "customer" || t === "supplier" || t === "investor") return t;
  if (t === "all" || t === "both") return "customer";
  return "unknown";
}
